/* KYO scroll story: scroll position drives the video's currentTime (never autoplay), then the
   section's own ingredient cards animate in. Fully reversible; inactive outside the viewport. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 600px)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  // Position of the opened capsule in the last frame (fraction of the video picture).
  const CAPSULE = { x: 0.82, y: 0.5 };
  // Mobile camera paths per animation: [moment (0-1), visible share of the video width, centre x, optional centre y (0-1)].
  // white_v1: capsule ends at 0.42-0.91. natural_v2: jar starts around 0.38-0.48, capsule ends at 0.20-0.81.
  const CAMERAS = {
    white_v1: [[0, 0.46, 0.5], [0.6, 0.46, 0.5], [0.75, 0.62, 0.52], [0.92, 0.62, 0.62], [1, 0.62, 0.62]],
    natural_v2: [[0, 0.48, 0.44, 0.47], [0.5, 0.48, 0.45, 0.47], [0.7, 0.54, 0.43, 0.48], [0.8, 0.62, 0.5, 0.5], [1, 0.62, 0.51, 0.5]],
  };

  class ScrollStory {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.kyo-story__track');
      this.stage = root.querySelector('.kyo-story__stage');
      this.video = root.querySelector('.kyo-story__video');
      this.header = root.querySelector('.ingredients-main__header');
      this.cards = [...root.querySelectorAll('.ingredient-card, .transparency-card')];
      this.videoEnd = clamp(parseFloat(root.dataset.videoEnd) || 0.82, 0.05, 1);
      this.cardsStart = clamp(parseFloat(root.dataset.cardsStart) || 0.84);
      this.cardsEnd = Math.max(this.cardsStart, clamp(parseFloat(root.dataset.cardsEnd) || 1));
      this.fade = root.dataset.fade === 'true';
      this.time = 0;
      this.frame = 0;
      this.near = false;
      this.revealed = null;
      this.sourceKey = '';
      this.objectUrl = '';

      this.cards.forEach((card, index) => card.style.setProperty('--i', index));
      // One seek at a time: while the browser is seeking, keep only the newest target.
      this.video.addEventListener('seeked', () => {
        if (this.pending === undefined) return;
        const next = this.pending;
        this.pending = undefined;
        this.seek(next);
      });
      this.onScroll = () => this.request();
      this.onMode = () => this.setMode();
      this.onSource = () => { if (this.near || reduced.matches) this.load(); this.layout(); this.request(); };
      this.onResize = () => { this.layout(); this.request(); };
      reduced.addEventListener('change', this.onMode);
      mobile.addEventListener('change', this.onSource);
      window.addEventListener('resize', this.onResize);
      // The section fades/slides in (theme reveal animation); measure again once it has settled.
      root.addEventListener('transitionend', event => { if (event.target === root) this.onResize(); });

      // Only work near the viewport: load the file 1.5 screens ahead, stop the loop when far away.
      this.observer = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near) { this.load(); window.addEventListener('scroll', this.onScroll, { passive: true }); this.request(); }
        else window.removeEventListener('scroll', this.onScroll);
      }, { rootMargin: '150% 0px' });
      this.observer.observe(root);
      this.setMode();
    }

    // Stage spans the full window width; the video is scaled so its empty outer margins (crop settings)
    // fall outside the stage, which is exactly as tall as the remaining picture and pinned vertically centred.
    // The cards are pulled up so that at "cards fully visible" they start near the top of the picture.
    layout() {
      const style = this.root.style;
      if (!this.root.classList.contains('is-live')) {
        ['width', 'marginLeft'].forEach(key => { this.stage.style[key] = ''; });
        ['width', 'height', 'left', 'top'].forEach(key => { this.video.style[key] = ''; });
        return;
      }
      // Usable height = window minus the sticky order bar (when shown), so nothing falls behind it.
      // Reserved from the start (also while the bar is still hidden) so the picture never jumps mid-scroll.
      const bar = document.querySelector('[data-sticky-buy] .sticky-buy__inner');
      const reserve = bar ? (parseFloat(getComputedStyle(bar).minHeight) || 0) + 1 : 0;
      const viewport = window.innerHeight - reserve;
      const pageWidth = document.documentElement.clientWidth;
      const ratio = (this.video.videoWidth / this.video.videoHeight) || 16 / 9;
      // Boxed layout: a centred cinematic card (max width, side gutter) instead of edge to edge. In the box the
      // whole picture always stays visible: if 16:9 at full box width does not fit the screen height, the box narrows.
      const data = this.root.dataset;
      const boxed = data.boxed === 'true';
      const gutter = boxed ? (parseFloat(mobile.matches ? data.boxGutterMobile : data.boxGutter) || 0) : 0;
      let boxWidth = boxed ? Math.min(parseFloat(data.boxMax) || pageWidth, pageWidth - 2 * gutter) : pageWidth;
      const boxRoom = boxed ? viewport - 2 * Math.max(gutter, 16) : viewport;
      // Measure where the stage actually lands (the content column may be shifted by a transform) and move it
      // to the intended x (0 for full width, centred for the box).
      const placeStage = width => {
        const targetX = (pageWidth - width) / 2;
        this.stage.style.width = `${width}px`;
        this.stage.style.marginLeft = '0px';
        for (let pass = 0, margin = 0; pass < 2; pass++) {
          margin -= this.stage.getBoundingClientRect().left - targetX;
          this.stage.style.marginLeft = `${margin}px`;
        }
      };
      // Box sits slightly above the middle of the free area (between the top of the screen and the order bar).
      const placeTop = stageHeight => boxed ? Math.max(gutter, (viewport - stageHeight) * 0.45) : (viewport - stageHeight) / 2;
      const crop = name => clamp((parseFloat(this.root.dataset[name]) || 0) / 100, 0, 0.45);
      const left = crop('cropLeft'), right = crop('cropRight'), top = crop('cropTop'), bottom = crop('cropBottom');
      // Mobile camera: a portrait screen gets a taller stage and a zoom that follows the animation (see camera()).
      this.camera = mobile.matches && this.root.dataset.mobileZoom === 'true' && this.sourceKey !== 'mobile-own';
      const cover = this.root.dataset.fit === 'cover';
      if (this.camera) {
        const preset = CAMERAS[this.root.dataset.camera] || CAMERAS.white_v1;
        // With a background in the video the picture must always fill the stage: stage <= picture height at the widest shot.
        const widest = Math.max(...preset.map(key => key[1]));
        // Always as tall as the picture at the widest shot, so the video fills the stage at every moment (no bars).
        const stageHeight = Math.min(boxRoom, boxWidth / widest / ratio);
        placeStage(boxWidth);
        this.cam = { pageWidth: boxWidth, stageHeight, ratio };
        const stageTop = placeTop(stageHeight);
        const track = this.track.offsetHeight;
        const settle = Math.min(stageTop, viewport * 0.12);
        style.setProperty('--story-stage-h', `${Math.round(stageHeight)}px`);
        style.setProperty('--story-stage-top', `${Math.round(stageTop)}px`);
        style.setProperty('--story-shift', `${Math.round(Math.max(0, stageTop + track - this.cardsEnd * Math.max(0, track - stageHeight) - settle))}px`);
        this.stageTop = stageTop;
        this.frameCamera();
        return;
      }
      if (boxed && boxWidth / ratio * (1 - top - bottom) > boxRoom) boxWidth = boxRoom / (1 - top - bottom) * ratio;
      placeStage(boxWidth);
      let width = boxWidth / (1 - left - right);
      let height = width / ratio;
      let stageHeight, offsetY;
      if (cover && !boxed) {
        // Full width; if taller than the screen, crop evenly top and bottom (never side bars).
        stageHeight = Math.min(viewport, height * (1 - top - bottom));
        offsetY = -top * height - (height * (1 - top - bottom) - stageHeight) / 2;
      } else {
        if (height * (1 - top - bottom) > viewport) { height = viewport / (1 - top - bottom); width = height * ratio; }
        stageHeight = height * (1 - top - bottom);
        offsetY = -top * height;
      }
      const offsetX = -left * width + (boxWidth - (1 - left - right) * width) / 2;
      Object.assign(this.video.style, { width: `${Math.round(width)}px`, height: `${Math.round(height)}px`, left: `${Math.round(offsetX)}px`, top: `${Math.round(offsetY)}px` });
      const stageTop = placeTop(stageHeight);
      const track = this.track.offsetHeight;
      const settle = Math.min(stageTop, viewport * 0.12);
      const shift = stageTop + track - this.cardsEnd * Math.max(0, track - stageHeight) - settle;
      style.setProperty('--story-stage-h', `${Math.round(stageHeight)}px`);
      style.setProperty('--story-stage-top', `${Math.round(stageTop)}px`);
      style.setProperty('--story-shift', `${Math.round(Math.max(0, shift))}px`);
      this.stageTop = stageTop;
    }

    // Mobile camera path: visible share of the video width and its centre, per moment of the animation.
    // Tight on the jar, briefly wider while the capsule flies in, then on the capsule with the jar partly
    // off-screen left. Measured from the animation's content box (capsule ends at 0.42-0.91; window 0.31-0.93 keeps a piece of the jar).
    frameCamera() {
      if (!this.camera || !this.cam) return;
      const keys = CAMERAS[this.root.dataset.camera] || CAMERAS.white_v1;
      const fraction = this.video.duration ? clamp(this.time / this.video.duration) : 0;
      let index = 0;
      while (index < keys.length - 2 && fraction > keys[index + 1][0]) index++;
      const [t0, w0, c0, y0 = 0.5] = keys[index], [t1, w1, c1, y1 = 0.5] = keys[index + 1];
      const mix = clamp((fraction - t0) / Math.max(0.0001, t1 - t0));
      const smooth = mix * mix * (3 - 2 * mix);
      const widthShare = w0 + (w1 - w0) * smooth;
      const center = c0 + (c1 - c0) * smooth;
      const centerY = y0 + (y1 - y0) * smooth;
      const { pageWidth, stageHeight, ratio } = this.cam;
      const width = pageWidth / widthShare;
      const height = width / ratio;
      this.video.style.width = `${width.toFixed(1)}px`;
      this.video.style.height = `${height.toFixed(1)}px`;
      this.video.style.left = `${(pageWidth / 2 - center * width).toFixed(1)}px`;
      // Keep the picture covering the stage when the vertical centre is moved.
      const topEdge = clamp(stageHeight / 2 - centerY * height, stageHeight - height, 0);
      this.video.style.top = `${(height >= stageHeight ? topEdge : (stageHeight - height) / 2).toFixed(1)}px`;
    }

    setMode() {
      const live = !reduced.matches;
      this.root.classList.toggle('is-live', live);
      this.layout();
      if (!live) {
        // Reduced motion: no pinning or scrubbing; the end frame as a still, cards shown normally.
        this.root.classList.add('is-revealed');
        this.stage.style.opacity = '';
        this.load();
      } else {
        this.revealed = null;
        this.request();
      }
    }

    sources() {
      const data = this.video.dataset;
      if (mobile.matches && (data.mobileSrc || data.mobileFallback)) return { key: 'mobile-own', list: [data.mobileSrc, data.mobileFallback], poster: data.mobilePoster };
      return { key: 'desktop', list: [data.desktopSrc, data.desktopFallback], poster: '' };
    }

    async load() {
      const { key, list, poster } = this.sources();
      if (this.sourceKey === key) return;
      this.sourceKey = key;
      if (poster) this.video.poster = poster;
      const urls = [...new Set(list.filter(Boolean))];
      // Load the whole file into memory first: every seek is then local, which keeps scrubbing smooth
      // in Chrome and Safari. If that is not possible, stream the file directly.
      let src = '';
      for (const url of urls) {
        try {
          const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
          if (!response.ok) continue;
          const blob = await response.blob();
          if (this.sourceKey !== key) return;
          if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
          this.objectUrl = URL.createObjectURL(blob);
          src = this.objectUrl;
          break;
        } catch (error) { /* try the next source */ }
      }
      if (!src) { src = urls[urls.length - 1] || ''; this.video.preload = 'auto'; }
      if (!src) return;
      this.video.src = src;
      this.video.load();
      this.video.addEventListener('loadedmetadata', () => {
        this.root.classList.add('has-video');
        this.layout();
        if (reduced.matches) this.seek(this.video.duration);
        else { this.time = this.targetTime(this.progress()); this.seek(this.time); }
        this.request();
      }, { once: true });
    }

    progress() {
      const box = this.track.getBoundingClientRect();
      const distance = box.height - this.stage.offsetHeight;
      return distance > 0 ? clamp(((this.stageTop || 0) - box.top) / distance) : 0;
    }

    targetTime(progress) {
      const duration = this.video.duration || 0;
      // Stop just before the end so the last frame stays visible (no "ended" state).
      return clamp(progress / this.videoEnd) * Math.max(0, duration - 0.04);
    }

    seek(time) {
      const video = this.video;
      if (!video.duration) return;
      const safe = clamp(time, 0, Math.max(0, video.duration - 0.04));
      if (video.seeking) { this.pending = safe; return; }
      if (Math.abs(video.currentTime - safe) > 0.004) video.currentTime = safe;
    }

    request() {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.tick());
    }

    tick() {
      this.frame = 0;
      if (reduced.matches || !this.root.classList.contains('is-live')) return;
      const progress = this.progress();

      // 1. Video follows the scroll position; light smoothing, then it stops exactly where scrolling stops.
      if (this.video.duration) {
        const target = this.targetTime(progress);
        const delta = target - this.time;
        this.time = Math.abs(delta) < 0.003 ? target : this.time + delta * 0.35;
        this.seek(this.time);
        this.frameCamera();
        if (this.time !== target) this.request();
      }

      // 2. Header and cards: in from the capsule once the start point is passed, out again when scrolling back.
      const revealed = progress >= this.cardsStart;
      if (revealed !== this.revealed) {
        if (revealed) this.aimFromCapsule();
        this.root.classList.toggle('is-revealed', revealed);
        this.revealed = revealed;
      }

      // 3. Optional: the jar and capsule fade out during the first half of the card window,
      //    so the cards emerge while the visual dissolves and settle on plain white.
      if (this.fade) {
        const span = Math.max(0.001, (this.cardsEnd - this.cardsStart) * 0.5);
        this.stage.style.opacity = String(1 - clamp((progress - this.cardsStart) / span));
      }
    }

    // Each card starts slightly shifted towards the opened capsule, so it seems to emerge from it.
    aimFromCapsule() {
      const video = this.video.getBoundingClientRect();
      const ratio = (this.video.videoWidth / this.video.videoHeight) || 16 / 9;
      let width = video.width, height = video.width / ratio;
      if (height > video.height) { height = video.height; width = height * ratio; }
      const capsuleX = this.root.dataset.camera === 'natural_v2' ? 0.6 : CAPSULE.x;
      const x = video.left + (video.width - width) / 2 + width * capsuleX;
      const y = video.top + (video.height - height) / 2 + height * CAPSULE.y;
      this.cards.forEach(card => {
        const box = card.getBoundingClientRect();
        const dx = clamp((x - (box.left + box.width / 2)) * 0.3, -220, 220);
        const dy = clamp((y - (box.top + Math.min(box.height, window.innerHeight) / 2)) * 0.3, -160, 160);
        card.style.setProperty('--story-fx', `${Math.round(dx)}px`);
        card.style.setProperty('--story-fy', `${Math.round(dy)}px`);
      });
    }

    destroy() {
      this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      reduced.removeEventListener('change', this.onMode);
      mobile.removeEventListener('change', this.onSource);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }
  }

  const stories = new WeakMap();
  const init = (scope = document) => scope.querySelectorAll('[data-kyo-story]').forEach(root => {
    if (!stories.has(root)) stories.set(root, new ScrollStory(root));
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-kyo-story]').forEach(root => { stories.get(root)?.destroy(); stories.delete(root); }));
  init();
})();
